/**
 * 各設計模板預設「主題（配色）」展示用 metadata
 * 須與前台 storefront SCSS 中 [data-storefront-theme] 的 token 一致。
 * 同步維護：api/src/services/commerce/storefrontThemePresetsByTemplate.js
 */

export const FALLBACK_STOREFRONT_THEME_PRESETS = [
  {
    value: 'default',
    label: '經典',
    description: '預設主色與對比；前台實作後對應各模板預設 token。',
    preview: 'linear-gradient(135deg, #4f46e5 0%, #312e81 100%)',
  },
  {
    value: 'minimal',
    label: '簡約淺色',
    description: '低對比、偏留白與內容層級。',
    preview: 'linear-gradient(135deg, #64748b 0%, #334155 100%)',
  },
  {
    value: 'bold',
    label: '亮色活潑',
    description: '飽和重點色，適合促銷與活動感。',
    preview: 'linear-gradient(135deg, #e11d48 0%, #881337 100%)',
  },
  {
    value: 'nature',
    label: '自然綠意',
    description: '綠／藍綠調，偏生活與食飲品牌。',
    preview: 'linear-gradient(135deg, #059669 0%, #14532d 100%)',
  },
];

const STYLE_MODERN = [
  {
    value: 'default',
    label: '亮藍主色',
    description: 'MarketPro 式亮藍按鈕＋深藍頂欄（#487fff／#121535）。',
    preview: 'linear-gradient(135deg, #487fff 0%, #121535 100%)',
  },
  {
    value: 'minimal',
    label: '石墨灰階',
    description: '灰主色與中性頂欄，低調俐落。',
    preview: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)',
  },
  {
    value: 'bold',
    label: '深藍強調',
    description: '高飽和藍按鈕與近黑藍頂欄。',
    preview: 'linear-gradient(135deg, #295cd8 0%, #11132e 100%)',
  },
  {
    value: 'nature',
    label: '湖水綠',
    description: '藍綠主色、淺綠底與綠灰框線。',
    preview: 'linear-gradient(135deg, #1c768b 0%, #19687a 100%)',
  },
];

const BAKERY = [
  {
    value: 'default',
    label: '焦糖暖棕',
    description: '烘焙預設琥珀主色與磚紅頂欄。',
    preview: 'linear-gradient(135deg, #b45309 0%, #9a3412 100%)',
  },
  {
    value: 'minimal',
    label: '石灰簡約',
    description: '淺灰底＋石灰主色與頂欄。',
    preview: 'linear-gradient(135deg, #a8a29e 0%, #57534e 100%)',
  },
  {
    value: 'bold',
    label: '暖橘強調',
    description: '亮橘主色與深橘頂欄。',
    preview: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
  },
  {
    value: 'nature',
    label: '苔綠自然',
    description: '深綠主色與森林綠頂欄。',
    preview: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
  },
];

const RETAIL = [
  {
    value: 'default',
    label: '品牌藍黑',
    description: 'Lockline 品牌藍搭配深藍黑，呈現穩定且專業的零售導購節奏。',
    preview: 'linear-gradient(135deg, #487fff 0%, #121535 100%)',
  },
  {
    value: 'minimal',
    label: '品牌霧灰',
    description: '低飽和藍灰，保留品牌調性且不搶商品內容。',
    preview: 'linear-gradient(135deg, #64748b 0%, #334155 100%)',
  },
  {
    value: 'bold',
    label: '品牌亮藍',
    description: '高亮品牌藍，強化活動 CTA 與促銷主視覺衝擊。',
    preview: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  },
  {
    value: 'nature',
    label: '藍綠平衡',
    description: '以品牌藍過渡青綠，兼顧科技感與親和力。',
    preview: 'linear-gradient(135deg, #1c768b 0%, #19687a 100%)',
  },
];

const SHOWCASE = [
  {
    value: 'default',
    label: '品牌藍紫',
    description: '品牌藍結合深紫，凸顯形象敘事與質感視覺。',
    preview: 'linear-gradient(135deg, #487fff 0%, #6d28d9 100%)',
  },
  {
    value: 'minimal',
    label: '霧藍畫廊',
    description: '低彩藍灰基調，讓圖片與內容敘事成為主角。',
    preview: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)',
  },
  {
    value: 'bold',
    label: '品牌電紫',
    description: '高飽和藍紫漸層，適合活動檔期與強烈視覺宣傳。',
    preview: 'linear-gradient(135deg, #5b7bff 0%, #7c3aed 100%)',
  },
  {
    value: 'nature',
    label: '品牌青綠',
    description: '品牌藍向青綠延伸，適合生活風格與永續敘事主題。',
    preview: 'linear-gradient(135deg, #1c768b 0%, #0f766e 100%)',
  },
];

const STANDARD = [
  {
    value: 'default',
    label: '經典藍',
    description: '標準模板預設藍色重點，適合通用商城版面。',
    preview: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)',
  },
  {
    value: 'minimal',
    label: '沉穩藍灰',
    description: '藍灰主色與中性邊框，降低對比、提升閱讀穩定性。',
    preview: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
  },
  {
    value: 'bold',
    label: '飽和藍',
    description: '高飽和藍主色，按鈕與連結對比更強。',
    preview: 'linear-gradient(135deg, #1d4ed8 0%, #172554 100%)',
  },
  {
    value: 'nature',
    label: '藍綠調',
    description: '藍綠主色與薄荷系邊框，風格更清爽自然。',
    preview: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
  },
];

const BY_TEMPLATE = {
  style_modern: STYLE_MODERN,
  bakery: BAKERY,
  retail: RETAIL,
  showcase: SHOWCASE,
  standard: STANDARD,
};

export function getStorefrontThemePresetsForTemplate(templateValue) {
  const key = String(templateValue ?? '')
    .trim()
    .toLowerCase();
  const set = BY_TEMPLATE[key];
  const src = set || FALLBACK_STOREFRONT_THEME_PRESETS;
  return src.map((t) => ({ ...t }));
}
